import { useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles, ArrowRight, ArrowUpRight, MapPin, ScrollText, Backpack, Wind,
  Compass, BookOpen, Crosshair, ShieldCheck, Zap, Lock, Send, Bookmark,
  Calendar, Ticket, CloudSun, ExternalLink,
} from "lucide-react";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { MEDIA } from "@/lib/media";

const IMG = {
  planning: "https://images.unsplash.com/photo-1779341437793-a01b8d8af48a?crop=entropy&cs=srgb&fm=jpg&q=80&w=900",
  regulations: "https://images.unsplash.com/photo-1534289907116-888fe70c4806?crop=entropy&cs=srgb&fm=jpg&q=80&w=900",
  gear: "https://images.unsplash.com/photo-1585365341416-307e4fa22402?crop=entropy&cs=srgb&fm=jpg&q=80&w=900",
  field: "https://images.unsplash.com/photo-1668357031982-10b0591efda4?crop=entropy&cs=srgb&fm=jpg&q=80&w=900",
};

const VALUE_POINTS = [
  { icon: Compass, title: "Context aware", desc: "Understands your hunt" },
  { icon: BookOpen, title: "Source grounded", desc: "Answers from official sources" },
  { icon: Crosshair, title: "Built for hunting", desc: "Backed by real experience" },
];

const CARDS = [
  {
    icon: MapPin, title: "Planning", img: IMG.planning,
    example: "Where can I hunt mule deer with 3 preference points?",
    tags: ["Draw odds", "Units", "Deadlines", "Point trends"],
  },
  {
    icon: ScrollText, title: "Regulations", img: IMG.regulations,
    example: "Can I use a muzzleloader in this unit?",
    tags: ["Official regulations", "Season dates", "Weapon restrictions", "Bag limits"],
  },
  {
    icon: Backpack, title: "Gear", img: IMG.gear,
    example: "Build me a $1,500 elk setup for late October.",
    tags: ["Pack", "Optics", "Clothing", "Boots", "Accessories", "More"],
  },
  {
    icon: Wind, title: "In the Field", img: IMG.field,
    example: "What should I change if tomorrow's wind shifts southwest?",
    tags: ["Weather", "Terrain", "Gear", "Hunt strategy", "Adjustments"],
  },
];

const TRUST = [
  { icon: ShieldCheck, title: "Official. Always.", desc: "Answers come from verified state agencies and trusted partners." },
  { icon: Zap, title: "Up to date.", desc: "HomeRange uses current indexed information and clearly represents source freshness." },
  { icon: Crosshair, title: "Built for hunters.", desc: "Designed around the questions hunters actually need answered." },
  { icon: Lock, title: "Your data. Your hunt.", desc: "Your hunts, conversations, notes, and plans stay tied to your account and private." },
];

const DEMO = {
  question: "I'm planning an Oregon archery elk hunt. Unit 61. September. What should I know?",
  rows: [
    { icon: Calendar, title: "Season & Regulations", text: "Archery season in Unit 61 typically runs Sep 1 – Sep 30. Check current ODFW rules for weapon and equipment restrictions." },
    { icon: Ticket, title: "Draw / Tag", text: "Unit 61 is a controlled draw unit. Application deadline is May 15. Success rate for archery elk is ~17% with 2 points." },
    { icon: CloudSun, title: "Conditions", text: "September temps average 45–70°F. Expect variable weather and occasional early snow at higher elevations." },
    { icon: Backpack, title: "Gear", text: "Focus on layering, quiet movement, and a quality 50–60L pack. I can build you a custom gear list if you'd like." },
  ],
};

const CARD = "#111214";
const BORDER = "#232326";
const ORANGE = "#FF5A1F";

export default function AskAI() {
  const { openAI } = useUI();
  const [q, setQ] = useState("");
  const ask = (text) => openAI((text || "").trim(), true);
  const submit = (e) => { e.preventDefault(); if (q.trim()) ask(q); };

  return (
    <div className="min-h-screen bg-[#0B0B0C] text-[#F5F5F5] font-sans" data-testid="ai-page">
      <SiteHeader />

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden border-b border-[#232326]" data-testid="ai-hero">
        <div className="absolute inset-0 z-0">
          <img src={MEDIA.exploreAI} alt="Hunter glassing a mountain ridge at dusk" className="w-full h-full object-cover object-right" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0C] via-[#0B0B0C]/92 to-[#0B0B0C]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-transparent to-[#0B0B0C]/40" />
        </div>
        <div className="relative z-10 max-w-[1180px] mx-auto px-5 sm:px-10 lg:px-16 pt-16 pb-14 lg:pt-24 lg:pb-20">
          <div className="max-w-[600px]">
            <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#FF5A1F]">HomeRange AI</span>
            <motion.h1
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mt-5"
            >
              Built for the<br />questions that<br />happen{" "}
              <span className="italic text-[#FF5A1F]">before<br />the hunt.</span>
            </motion.h1>
            <p className="text-[#B9B9B9] text-base sm:text-lg leading-relaxed mt-6 max-w-[480px]">
              From draw odds and regulations to gear, terrain, and hunt planning — ask HomeRange AI and get answers grounded in trusted sources.
            </p>

            {/* AI input */}
            <form onSubmit={submit} className="mt-8 max-w-[560px]" data-testid="ai-hero-form">
              <div className="rounded-2xl border border-[#2c2c30] bg-[#111214]/90 backdrop-blur-md p-4 sm:p-5 shadow-2xl">
                <div className="flex items-center gap-3">
                  <Sparkles size={18} className="text-[#FF5A1F] shrink-0" />
                  <input
                    value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="Ask HomeRange anything..."
                    className="flex-1 bg-transparent outline-none text-[15px] text-[#F5F5F5] placeholder:text-[#7A7A7E]"
                    data-testid="ai-hero-input"
                  />
                  <button type="submit" aria-label="Ask HomeRange AI"
                    className="shrink-0 w-11 h-11 rounded-full grid place-items-center text-white transition-colors bg-[#FF5A1F] hover:bg-[#FF6D33]"
                    data-testid="ai-hero-submit">
                    <ArrowRight size={19} />
                  </button>
                </div>
                <div className="flex items-center gap-2 mt-3 pl-[30px] text-[13px] text-[#8B8B8F]">
                  <span>Try:</span>
                  <button type="button" onClick={() => ask("What are my best elk draw options in Montana with 4 points?")}
                    className="italic text-left hover:text-[#F5F5F5] transition-colors" data-testid="ai-hero-example">
                    &ldquo;What are my best elk draw options in Montana with 4 points?&rdquo;
                  </button>
                </div>
              </div>
            </form>

            {/* 3 value points */}
            <div className="mt-9 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#232326] rounded-xl overflow-hidden border border-[#232326]" data-testid="ai-value-points">
              {VALUE_POINTS.map((v) => {
                const Icon = v.icon;
                return (
                  <div key={v.title} className="bg-[#0B0B0C]/70 px-5 py-4 flex items-start gap-3">
                    <Icon size={18} className="text-[#FF5A1F] mt-0.5 shrink-0" />
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#F5F5F5]">{v.title}</div>
                      <div className="text-[13px] text-[#8B8B8F] mt-0.5">{v.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============ ONE ASSISTANT. YOUR WHOLE HUNT. ============ */}
      <section className="max-w-[1180px] mx-auto px-5 sm:px-10 lg:px-16 py-16 lg:py-20">
        <div className="text-center">
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">One assistant. Your whole hunt.</h2>
          <div className="mx-auto mt-4 h-[3px] w-14 rounded-full bg-[#FF5A1F]" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12" data-testid="ai-cards">
          {CARDS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="group flex flex-col rounded-xl overflow-hidden border border-[#232326] bg-[#111214] hover:border-[#3a3a3f] transition-colors"
                data-testid={`ai-card-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="relative h-36 overflow-hidden">
                  <img src={c.img} alt={c.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-[1.04] transition-all duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#111214] via-[#111214]/30 to-transparent" />
                  <span className="absolute left-4 bottom-3 w-10 h-10 rounded-full grid place-items-center bg-[#0B0B0C]/80 border border-[#FF5A1F]/50 text-[#FF5A1F] backdrop-blur-sm">
                    <Icon size={18} />
                  </span>
                </div>
                <div className="flex flex-col flex-1 p-5">
                  <h3 className="font-serif text-2xl">{c.title}</h3>
                  <p className="text-[13px] text-[#B9B9B9] italic mt-2 leading-relaxed">&ldquo;{c.example}&rdquo;</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-4">
                    {c.tags.map((t) => (
                      <span key={t} className="flex items-center gap-1.5 text-[12px] text-[#8B8B8F]">
                        <span className="w-1 h-1 rounded-full bg-[#FF5A1F]/70" />{t}
                      </span>
                    ))}
                  </div>
                  <button onClick={() => ask(c.example)}
                    className="mt-auto pt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#FF5A1F] hover:gap-2.5 transition-all"
                    data-testid={`ai-card-try-${c.title.toLowerCase().replace(/\s+/g, "-")}`}>
                    Try it <ArrowRight size={14} />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ============ ASK. PLAN. HUNT SMARTER + TRUST ============ */}
      <section className="max-w-[1180px] mx-auto px-5 sm:px-10 lg:px-16 pb-4">
        <div className="rounded-2xl border border-[#232326] bg-[#0E0E10] p-6 sm:p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-10 lg:gap-14">
          {/* Conversation demo */}
          <div data-testid="ai-conversation-demo">
            <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#FF5A1F]">Ask. Plan. Hunt smarter.</span>

            {/* user bubble */}
            <div className="flex items-start gap-3 mt-6">
              <span className="w-8 h-8 rounded-full bg-[#232326] grid place-items-center text-[11px] font-bold text-[#B9B9B9] shrink-0">YOU</span>
              <div className="rounded-xl rounded-tl-none bg-[#1a1a1d] border border-[#232326] px-4 py-3 text-[14px] text-[#E7E7E7]">
                {DEMO.question}
              </div>
            </div>

            {/* AI response */}
            <div className="flex items-start gap-3 mt-5">
              <span className="w-8 h-8 rounded-full bg-[#FF5A1F] grid place-items-center text-[11px] font-bold text-white shrink-0">HR</span>
              <div className="flex-1 rounded-xl rounded-tl-none bg-[#111214] border border-[#232326] p-5">
                <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8B8B8F]">
                  HomeRange AI <span className="text-[#5f5f63]">· Example</span>
                </div>
                <p className="font-serif text-xl mt-2">Here&apos;s what I&apos;d start with.</p>
                <div className="mt-4 space-y-4">
                  {DEMO.rows.map((r) => {
                    const Icon = r.icon;
                    return (
                      <div key={r.title} className="flex items-start gap-3">
                        <Icon size={15} className="text-[#FF5A1F] mt-1 shrink-0" />
                        <div>
                          <div className="text-[13px] font-semibold text-[#F5F5F5]">{r.title}</div>
                          <div className="text-[13px] text-[#B9B9B9] leading-relaxed mt-0.5">{r.text}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-5 pt-4 border-t border-[#232326]">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8B8B8F]">Sources</div>
                  <a href="https://myodfw.com" target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-[13px] text-[#FF5A1F] hover:underline mt-1.5" data-testid="ai-demo-source">
                    Oregon Department of Fish &amp; Wildlife <ExternalLink size={12} />
                  </a>
                </div>
                <button onClick={() => ask(DEMO.question)}
                  className="mt-5 inline-flex items-center gap-2 rounded-lg border border-[#FF5A1F]/60 text-[#FF5A1F] hover:bg-[#FF5A1F] hover:text-white px-4 py-2.5 text-[13px] font-semibold transition-colors"
                  data-testid="ai-demo-save">
                  <Bookmark size={15} /> Save to My Hunt
                </button>
              </div>
            </div>
          </div>

          {/* Trust panel */}
          <div className="lg:border-l lg:border-[#232326] lg:pl-14" data-testid="ai-trust-panel">
            <h3 className="font-serif text-[26px] leading-tight">Why hunters trust<br />HomeRange AI</h3>
            <div className="mt-7 space-y-7">
              {TRUST.map((t) => {
                const Icon = t.icon;
                return (
                  <div key={t.title} className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded-full border border-[#FF5A1F]/40 grid place-items-center text-[#FF5A1F] shrink-0">
                      <Icon size={17} />
                    </span>
                    <div>
                      <div className="font-serif text-lg text-[#F5F5F5]">{t.title}</div>
                      <p className="text-[13px] text-[#B9B9B9] leading-relaxed mt-1">{t.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============ BOTTOM CTA ============ */}
      <section className="max-w-[1180px] mx-auto px-5 sm:px-10 lg:px-16 py-12">
        <div className="rounded-2xl border border-[#232326] bg-[#111214] px-6 sm:px-10 py-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5" data-testid="ai-bottom-cta">
          <p className="font-serif text-xl sm:text-2xl text-[#F5F5F5]">
            AI that understands hunting. Built on experience. Backed by sources.
          </p>
          <button onClick={() => openAI("", false)}
            className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#FF5A1F] hover:bg-[#FF6D33] text-white px-6 py-3.5 text-sm font-semibold transition-colors"
            data-testid="ai-bottom-cta-btn">
            Ask HomeRange AI <ArrowUpRight size={17} />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
